<template>
  <section class="content-panel">
    <div class="section-head">
      <div>
        <h2>应用配置</h2>
        <p>管理客户应用及广告位参数</p>
      </div>
      <div class="head-actions">
        <CustomerRemoteSelect
          v-model="filterCustomerId"
          :token="admin.token.value"
          placeholder="按客户筛选"
          width="180px"
          @change="onFilterCustomer"
          @select="onCustomerSelect"
        />
        <el-input
          v-model.trim="keyword"
          placeholder="搜索应用名/包名"
          clearable
          class="search-input"
          :prefix-icon="Search"
          @keyup.enter="search"
          @clear="search"
        />
        <el-button type="primary" @click="search">查询</el-button>
        <el-button type="primary" @click="openCreate">
          <el-icon><Plus /></el-icon>创建应用
        </el-button>
      </div>
    </div>

    <el-table :data="admin.apps.value" stripe>
      <el-table-column type="index" label="#" width="56" />
      <el-table-column label="所属客户" min-width="140">
        <template #default="{ row }">{{ admin.customerName(row.customerId) }}</template>
      </el-table-column>
      <el-table-column prop="appName" label="应用名称" min-width="140" />
      <el-table-column prop="packageName" label="应用包名" min-width="180" show-overflow-tooltip />
      <el-table-column label="版本号" width="90">
        <template #default="{ row }">
          <el-tag v-if="row.versionCode" type="warning" effect="plain">v{{ row.versionCode }}</el-tag>
          <span v-else class="muted">-</span>
        </template>
      </el-table-column>
      <el-table-column label="广告位 ID" min-width="220" show-overflow-tooltip>
        <template #default="{ row }">
          <span v-if="row.rewardPlacement || row.interstitialPlacement || row.bannerPlacement">
            激励:{{ row.rewardPlacement || '—' }} 插屏:{{ row.interstitialPlacement || '—' }} Banner:{{ row.bannerPlacement || '—' }}
          </span>
          <span v-else class="muted">未配置</span>
        </template>
      </el-table-column>
      <el-table-column label="阿里云风控" min-width="150">
        <template #default="{ row }">
          <template v-if="row.riskEnabled === 1">
            <el-tag type="success" effect="light">已开启</el-tag>
            <!-- <span class="muted" style="margin-left: 6px">{{ row.riskConfigName || '未绑定' }}</span> -->
          </template>
          <el-tag v-else type="info" effect="light">已关闭</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="收益占比" width="100">
        <template #default="{ row }">{{ row.revenueShareRatio ?? 90 }}%</template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'" effect="light">
            {{ row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="80" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
        </template>
      </el-table-column>
      <template #empty><el-empty description="暂无应用配置" /></template>
    </el-table>

    <div class="panel-footer">
      <el-pagination
        :current-page="admin.appPagination.page"
        :page-size="admin.appPagination.size"
        :page-sizes="[10, 20, 50, 100]"
        :total="admin.appPagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        background
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>

    <el-dialog v-model="dialog" :title="dialogTitle" width="640px">
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <div class="form-row">
          <el-form-item label="所属客户" prop="customerId">
            <CustomerRemoteSelect
              v-model="form.customerId"
              :token="admin.token.value"
              placeholder="选择客户"
              width="100%"
              :clearable="false"
              @select="onCustomerSelect"
            />
          </el-form-item>
          <el-form-item label="应用名称" prop="appName">
            <el-input v-model.trim="form.appName" />
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item label="应用包名" prop="packageName">
            <el-input v-model.trim="form.packageName" />
          </el-form-item>
          <el-form-item label="版本号">
            <el-input-number v-model="form.versionCode" :min="1" controls-position="right" />
          </el-form-item>
        </div>
        <el-divider content-position="left">阿里云风控</el-divider>
        <div class="form-row">
          <el-form-item label="开启风控">
            <el-switch v-model="form.riskEnabled" :active-value="1" :inactive-value="0" />
          </el-form-item>
          <el-form-item label="风控公司配置" prop="riskConfigId">
            <el-select
              v-model="form.riskConfigId"
              placeholder="选择公司风控配置"
              clearable
              filterable
              style="width: 100%"
              :disabled="form.riskEnabled !== 1"
            >
              <el-option
                v-for="opt in admin.riskConfigOptions.value"
                :key="opt.id"
                :label="opt.name"
                :value="opt.id"
              />
            </el-select>
          </el-form-item>
        </div>
        <el-divider content-position="left">广告位配置</el-divider>
        <div class="form-row">
          <el-form-item label="激励广告位 ID">
            <el-input v-model.trim="form.rewardPlacement" />
          </el-form-item>
          <el-form-item label="插屏广告位 ID">
            <el-input v-model.trim="form.interstitialPlacement" />
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item label="Banner 广告位 ID">
            <el-input v-model.trim="form.bannerPlacement" />
          </el-form-item>
          <el-form-item label="安装包地址">
            <el-input v-model.trim="form.downloadUrl" />
          </el-form-item>
        </div>
        <el-divider content-position="left">收益与次数</el-divider>
        <div class="form-row">
          <el-form-item label="用户收益占比(%)" prop="revenueShareRatio">
            <el-input-number
              v-model="form.revenueShareRatio"
              :min="0"
              :max="10000"
              controls-position="right"
            />
            <div class="form-tip">百分制，默认 90。例如 90 表示按 90% 计收益，200 表示按 200% 计。仅影响修改后新上报的数据。</div>
          </el-form-item>
          <el-form-item label="激励视频每日限制">
            <el-input-number v-model="form.rewardDailyLimit" :min="0" placeholder="不限制" controls-position="right" />
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item label="插屏广告每日限制">
            <el-input-number v-model="form.interstitialDailyLimit" :min="0" placeholder="不限制" controls-position="right" />
          </el-form-item>
          <el-form-item label="Banner 广告每日限制">
            <el-input-number v-model="form.bannerDailyLimit" :min="0" placeholder="不限制" controls-position="right" />
          </el-form-item>
        </div>
        <div class="form-row">
          <el-form-item>
            <template #label>
              <span style="color: #909399; font-size: 12px;">每日限制：留空或 0 表示不限制</span>
            </template>
          </el-form-item>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dialog = false">取消</el-button>
        <el-button type="primary" @click="submit">{{ editingId ? '保存修改' : '确认创建' }}</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { Plus, Search } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import CustomerRemoteSelect from '@/components/CustomerRemoteSelect.vue'

const props = defineProps({ admin: Object })

const dialog = ref(false)
const editingId = ref(null)
const formRef = ref()
const dialogTitle = computed(() => (editingId.value ? '编辑应用' : '创建应用'))
const keyword = ref(props.admin.appPagination.keyword || '')
const filterCustomerId = ref(props.admin.appPagination.customerId || null)

const empty = () => ({
  customerId: null, appName: '', packageName: '', versionCode: null,
  riskEnabled: 0, riskConfigId: null,
  rewardPlacement: '', interstitialPlacement: '', bannerPlacement: '',
  rewardDailyLimit: null, interstitialDailyLimit: null, bannerDailyLimit: null,
  revenueShareRatio: 90,
  downloadUrl: ''
})
const form = reactive(empty())

const rules = {
  customerId: [{ required: true, message: '请选择客户' }],
  appName: [{ required: true, message: '请输入应用名称' }],
  packageName: [{ required: true, message: '请输入应用包名' }],
  revenueShareRatio: [
    { required: true, message: '请填写用户收益占比' },
    {
      validator: (_r, v, cb) => {
        if (v == null || v === '' || Number(v) < 0) {
          cb(new Error('收益占比不能为空且不能为负数'))
          return
        }
        cb()
      },
      trigger: 'change'
    }
  ],
  riskConfigId: [{
    validator: (_r, v, cb) => {
      if (form.riskEnabled === 1 && (v == null || v === '')) {
        cb(new Error('开启风控时请选择公司配置'))
        return
      }
      cb()
    },
    trigger: 'change'
  }]
}

watch(() => form.riskEnabled, (v) => {
  if (v !== 1) form.riskConfigId = null
})

function onCustomerSelect(item) {
  if (item) props.admin.rememberCustomers([item])
}

async function search() {
  try {
    await props.admin.loadApps({
      page: 1,
      keyword: keyword.value,
      customerId: filterCustomerId.value
    })
  } catch (error) {
    ElMessage.error(error.message)
  }
}

async function onFilterCustomer(id) {
  filterCustomerId.value = id
  await search()
}

async function onPageChange(page) {
  try {
    await props.admin.loadApps({ page })
  } catch (error) {
    ElMessage.error(error.message)
  }
}

async function onSizeChange(size) {
  try {
    await props.admin.loadApps({ page: 1, size })
  } catch (error) {
    ElMessage.error(error.message)
  }
}

const openCreate = async () => {
  editingId.value = null
  Object.assign(form, empty())
  await props.admin.loadRiskConfigOptions?.()
  dialog.value = true
}

const openEdit = async (row) => {
  editingId.value = row.id
  if (row.customerPhone) {
    props.admin.rememberCustomers([{ id: row.customerId, phone: row.customerPhone }])
  }
  await props.admin.loadRiskConfigOptions?.()
  Object.assign(form, {
    customerId: row.customerId,
    appName: row.appName,
    packageName: row.packageName,
    versionCode: row.versionCode ?? null,
    riskEnabled: row.riskEnabled ?? 0,
    riskConfigId: row.riskConfigId ?? null,
    rewardPlacement: row.rewardPlacement || '',
    interstitialPlacement: row.interstitialPlacement || '',
    bannerPlacement: row.bannerPlacement || '',
    rewardDailyLimit: row.rewardDailyLimit ?? null,
    interstitialDailyLimit: row.interstitialDailyLimit ?? null,
    bannerDailyLimit: row.bannerDailyLimit ?? null,
    revenueShareRatio: row.revenueShareRatio ?? 90,
    downloadUrl: row.downloadUrl || ''
  })
  dialog.value = true
}

const submit = async () => {
  if (!await formRef.value.validate().catch(() => false)) return
  const payload = {
    ...form,
    riskEnabled: form.riskEnabled,
    riskConfigId: form.riskEnabled === 1 ? form.riskConfigId : null
  }
  if (editingId.value) {
    await props.admin.updateApp({ ...payload, id: editingId.value })
  } else {
    await props.admin.createApp(payload)
  }
  Object.assign(form, empty())
  editingId.value = null
  dialog.value = false
}

onMounted(() => {
  props.admin.loadRiskConfigOptions?.()
})
</script>
